import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { finalize, Observable } from 'rxjs';
import {
  DynamicDialogRef,
  DynamicDialogConfig,
  DialogService,
} from 'primeng/dynamicdialog';
import { IntegrationSystem } from 'src/app/shared/models/integration.models';
import { IntegrationService } from 'src/app/shared/services/integration.service';
import { AppToastService } from 'src/app/shared/services/app-toast.service';
import { TenantService } from 'src/app/core/services/tenant.service';

@Component({
  selector: 'app-integration-system-modal',
  templateUrl: './integration-system-modal.component.html',
  standalone: false,
  providers: [DialogService],
})
export class IntegrationSystemModalComponent implements OnInit {
  editingId?: number;
  saving = false;
  /** Skeleton do modal enquanto o get de abertura (tenants) não termina. */
  initialLoading = true;
  tenants: { id: string; name: string }[] = [];

  form: FormGroup = this.fb.group({
    tenantId: ['', Validators.required],
    code: ['', Validators.required],
    name: ['', Validators.required],
    enabled: [true],
    configJson: [''],
  });

  constructor(
    private readonly fb: FormBuilder,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private readonly integrationService: IntegrationService,
    private readonly toastService: AppToastService,
    private readonly tenantService: TenantService,
  ) {}

  ngOnInit(): void {
    const system: IntegrationSystem | undefined = this.config.data?.system;
    this.editingId = system?.id;

    this.form.reset({
      tenantId: system?.tenantId || '',
      code: system?.code || '',
      name: system?.name || '',
      enabled: system?.enabled ?? true,
      configJson: system?.configJson || '',
    });

    if (this.editingId) {
      this.form.get('tenantId')?.disable();
    }

    this.loadTenants();
  }

  loadTenants(): void {
    this.tenantService
      .getTenants(1000, 0, '')
      .pipe(finalize(() => (this.initialLoading = false)))
      .subscribe({
        next: (data: any) => {
          this.tenants = (data?.tenants || [])
            .map((t: any) => ({ id: t.id, name: t.name }))
            .sort((a: any, b: any) => a.name.localeCompare(b.name));
        },
      });
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving = true;
    const raw = this.form.getRawValue();
    const payload = {
      code: String(raw.code || '').trim(),
      name: String(raw.name || '').trim(),
      enabled: !!raw.enabled,
      configJson: raw.configJson ? String(raw.configJson).trim() : null,
      tenantId: raw.tenantId || null,
    };

    const request$: Observable<unknown> = this.editingId
      ? this.integrationService.updateSystem(this.editingId, payload)
      : this.integrationService.addSystem(payload);

    request$.pipe(finalize(() => (this.saving = false))).subscribe({
      next: () => {
        this.toastService.success(
          this.editingId ? 'System updated' : 'System created',
          'Integration system saved successfully.',
        );
        this.ref.close('success');
      },
    });
  }
}
